import { FC, useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";
import axios from "../../../utils/axios";
import { useAppSelector } from "../../../store/hooks";

// Reusable SKU picker. Returns the full SKU object on change so callers can read
// baseUnitName / unitConversions (for unit dropdowns) and _id.
type SkuOption = { label: string; value: string; sku: any };

// Items fetched per request. The list is searched server-side, so this only
// needs to cover the initial "browse" dropdown, not the whole catalogue.
const PAGE_SIZE = 20;

const extractList = (data: any): any[] =>
  Array.isArray(data) ? data : data?.inventories || data?.skus || data?.data || [];

interface Props {
  value?: any; // skuId or sku object
  onChange: (sku: any | null) => void;
  label?: string;
  size?: "small" | "medium";
  // When true, renders as a borderless "c-text-field-3" pill (matches the Items filter bar).
  borderless?: boolean;
  width?: number | string;
  // When true, typing also matches items by barcode (scan or manual code entry).
  barcodeSearch?: boolean;
}

const SkuSelect: FC<Props> = ({ value, onChange, label = "Item (SKU)", size = "small", borderless = false, width, barcodeSearch = false }) => {
  const { selectedAccount } = useAppSelector((state) => state.auth);
  const [options, setOptions] = useState<SkuOption[]>([]);
  const [barcodeOptions, setBarcodeOptions] = useState<SkuOption[]>([]);
  const [inputText, setInputText] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  // Only a small first page is loaded; typing narrows the list on the server
  // (`filter` matches name / description / itemCode) instead of shipping the
  // whole catalogue to the browser.
  useEffect(() => {
    let active = true;
    const accountId = selectedAccount?._id;
    if (!accountId) return;
    const q = searchText.trim();
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `/api/v1/sku/account/${accountId}?limit=${PAGE_SIZE}${q ? `&filter=${encodeURIComponent(q)}` : ""}`
        );
        const list = extractList(res.data);
        if (active) {
          setOptions(
            list.map((s: any) => ({
              label: `${s.name}${s.itemCode ? ` (${s.itemCode})` : ""}`,
              value: s._id,
              sku: s,
            }))
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    }, q ? 300 : 0);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [selectedAccount, searchText]);

  // Barcode lookup: when enabled, debounce the typed text and fetch matching items by
  // their barcode so a damaged/unscanned label can be found by typing the number.
  useEffect(() => {
    if (!barcodeSearch) return;
    const q = inputText.trim();
    if (q.length < 2) { setBarcodeOptions([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/v1/erp/barcode/search?q=${encodeURIComponent(q)}`);
        const rows = Array.isArray(res.data) ? res.data : [];
        if (active) {
          setBarcodeOptions(
            rows
              .filter((r: any) => r.sku)
              .map((r: any) => ({ label: `🔎 ${r.code} — ${r.sku.name}`, value: r.sku._id, sku: r.sku }))
          );
        }
      } catch {
        if (active) setBarcodeOptions([]);
      }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [inputText, barcodeSearch]);

  const currentId = value?._id || value || "";

  // Callers usually pass just an id, and that item may fall outside the loaded
  // page — resolve it on its own so the field never renders blank.
  const [resolved, setResolved] = useState<any>(null);
  useEffect(() => {
    if (!currentId || value?._id || options.some((o) => o.value === currentId)) return;
    if (resolved?._id === currentId) return;
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`/api/v1/sku/${currentId}`);
        const sku = res.data?.data || res.data;
        if (active && sku?._id) setResolved(sku);
      } catch {
        /* leave it unresolved — the field just shows empty */
      }
    })();
    return () => { active = false; };
  }, [currentId, value, options, resolved]);

  // Barcode matches first, then the name/code list (deduped by sku id).
  const mergedOptions = barcodeSearch
    ? [...barcodeOptions, ...options.filter((o) => !barcodeOptions.some((b) => b.value === o.value))]
    : options;
  // The selected item may not be in the current (search-narrowed) page, so fall
  // back to the value object itself or the separately resolved SKU.
  const fallbackSku = value?._id ? value : resolved?._id === currentId ? resolved : null;
  const selected =
    mergedOptions.find((o) => o.value === currentId) ||
    (fallbackSku
      ? {
          label: `${fallbackSku.name}${fallbackSku.itemCode ? ` (${fallbackSku.itemCode})` : ""}`,
          value: fallbackSku._id,
          sku: fallbackSku,
        }
      : null);

  return (
    <Autocomplete
      options={mergedOptions}
      loading={loading}
      // Results are already filtered by the server — don't filter them again
      // client-side, or matches outside the loaded page would be hidden.
      filterOptions={(o) => o}
      getOptionLabel={(o: any) => o.label || ""}
      isOptionEqualToValue={(o: any, v: any) => o.value === v.value}
      value={selected}
      onChange={(_e, v: any) => onChange(v ? v.sku : null)}
      onInputChange={(_e, v, reason) => {
        setInputText(v);
        // Ignore the input reset that follows a selection — it would trigger a
        // redundant search for the label we just picked.
        if (reason !== "reset") setSearchText(v);
      }}
      style={width ? { width } : undefined}
      renderInput={(params) =>
        borderless ? (
          <TextField
            {...params}
            className="c-text-field-3"
            variant="standard"
            size={size}
            placeholder={label}
            InputProps={{ ...params.InputProps, disableUnderline: true, style: { fontSize: 14 } }}
          />
        ) : (
          <TextField {...params} label={label} size={size} />
        )
      }
    />
  );
};

export default SkuSelect;
